/**
 * CyBoard Location Service
 *
 * Wraps `@capacitor/geolocation` for native and `navigator.geolocation` for web.
 * Handles permission prompt, caches the last-known device coords in
 * localStorage, and exposes helpers that DiscoverPage/HomePage can call.
 *
 * Design goals:
 *   - Never block first paint on a slow GPS fix.
 *   - Silent-fail if permission denied (caller shows the banner).
 *   - Auto-refresh when the app resumes from background (Capacitor App plugin).
 *   - No hard requirement on Capacitor being present (web PWA still works).
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_COORDS = 'cyb_device_coords_v1';
const LS_PERM = 'cyb_geo_permission_v1'; // 'granted' | 'denied' | 'prompt'
const COORDS_MAX_AGE_MS = 15 * 60 * 1000; // 15 min — feels fresh on app resume
const FIX_TIMEOUT_MS = 8000;

const isNative = () => {
  try {
    return !!(
      window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    );
  } catch {
    return false;
  }
};

async function getCapacitorGeo() {
  try {
    const mod = await import('@capacitor/geolocation');
    return mod.Geolocation;
  } catch {
    return null;
  }
}

/** Reads the cached device coords (may be stale). */
export function getCachedCoords() {
  try {
    const raw = localStorage.getItem(LS_COORDS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.lat !== 'number' || typeof parsed.lon !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getPermissionState() {
  return localStorage.getItem(LS_PERM) || 'prompt';
}

function saveCoords(lat, lon) {
  const doc = { lat, lon, ts: Date.now() };
  try {
    localStorage.setItem(LS_COORDS, JSON.stringify(doc));
  } catch {}
  return doc;
}

/** Best-effort POST to backend so future feed calls have the coords server-side too. */
async function pushCoordsToServer(lat, lon) {
  try {
    await axios.post(
      `${API_URL}/api/users/me/location-coords`,
      { lat, lon },
      { withCredentials: true }
    );
  } catch {
    // silent — coord push is a nice-to-have; feed also accepts coords via query.
  }
}

/**
 * Prompt the user for GPS permission (native) or geolocation (web) and
 * fetch a single position. Returns { lat, lon } on success, null on denial/failure.
 */
export async function requestAndFetchLocation({ silent = false } = {}) {
  try {
    if (isNative()) {
      const Geolocation = await getCapacitorGeo();
      if (!Geolocation) throw new Error('Geolocation plugin missing');

      // Ask for permission if not already granted.
      try {
        const status = await Geolocation.checkPermissions();
        if (status.location !== 'granted' && status.coarseLocation !== 'granted') {
          const req = await Geolocation.requestPermissions({ permissions: ['location'] });
          if (req.location !== 'granted' && req.coarseLocation !== 'granted') {
            localStorage.setItem(LS_PERM, 'denied');
            return null;
          }
        }
      } catch {
        // Some Capacitor versions throw if permissions API unsupported; try get anyway.
      }

      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: FIX_TIMEOUT_MS,
        maximumAge: COORDS_MAX_AGE_MS,
      });
      const lat = pos.coords.latitude;
      const lon = pos.coords.longitude;
      localStorage.setItem(LS_PERM, 'granted');
      saveCoords(lat, lon);
      pushCoordsToServer(lat, lon);
      return { lat, lon };
    }

    // Web PWA fallback
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      localStorage.setItem(LS_PERM, 'denied');
      return null;
    }
    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          localStorage.setItem(LS_PERM, 'granted');
          saveCoords(lat, lon);
          pushCoordsToServer(lat, lon);
          resolve({ lat, lon });
        },
        () => {
          localStorage.setItem(LS_PERM, 'denied');
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: FIX_TIMEOUT_MS, maximumAge: COORDS_MAX_AGE_MS }
      );
    });
  } catch (e) {
    if (!silent) console.warn('[geo] fetch failed:', e?.message || e);
    return null;
  }
}

/**
 * Returns cached coords if fresh, else prompts + refetches. Ideal for
 * DiscoverPage on mount and for App-resume refresh.
 */
export async function ensureFreshCoords({ silent = false } = {}) {
  const cached = getCachedCoords();
  if (cached && Date.now() - cached.ts < COORDS_MAX_AGE_MS) return cached;
  return requestAndFetchLocation({ silent });
}

/**
 * Wire the Capacitor App plugin so we refresh coords every time the app comes
 * back to the foreground. Silent so we don't spam re-prompts on denied users.
 * Call ONCE from AuthContext / App.js.
 */
export async function installLocationAutoRefresh() {
  try {
    if (!isNative()) return;
    const { App } = await import('@capacitor/app');
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Small delay so the app finishes waking before we ask for a GPS fix.
        setTimeout(() => ensureFreshCoords({ silent: true }), 400);
      }
    });
    App.addListener('resume', () => {
      setTimeout(() => ensureFreshCoords({ silent: true }), 400);
    });
  } catch {
    /* App plugin missing on web — that's fine. */
  }
}
